if (!customElements.get('geo-prompt')) {
  customElements.define('geo-prompt', class GeoPrompt extends HTMLElement {
    constructor() {
      super();
      this.targetCountry = (this.dataset.targetCountry || 'US').toUpperCase();
      this.targetUrl = this.dataset.targetUrl || '';
      this.dismissCookie = 'ak_geo_prompt_dismissed';
      this.cacheKey = 'ak_geo_country_v1';
      this.cacheTtlMs = 24 * 60 * 60 * 1000;
      this.dismissDays = 30;
      this.openDelayMs = 600;
    }

    connectedCallback() {
      if (window.Shopify && window.Shopify.designMode) return;
      if (window.location.pathname === '/challenge') return;

      const params = new URLSearchParams(window.location.search);
      this.testMode = params.get('geo_prompt_test') === '1';

      if (!this.testMode && this.getCookie(this.dismissCookie)) return;

      this.bindEvents();
      if (this.testMode) {
        window.setTimeout(() => this.open(), 100);
      } else {
        this.detectAndMaybeShow();
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-geo-prompt-dismiss]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.dismiss();
        });
      });

      const accept = this.querySelector('[data-geo-prompt-accept]');
      if (accept) {
        if (this.targetUrl) accept.setAttribute('href', this.targetUrl);
        accept.addEventListener('click', () => {
          this.setCookie(this.dismissCookie, this.dismissDays);
        });
      }

      this.onKeydown = (e) => {
        if (e.key === 'Escape') {
          this.dismiss();
          return;
        }
        if (e.key === 'Tab') this.trapFocus(e);
      };
    }

    async detectAndMaybeShow() {
      let country = this.readCachedCountry();
      if (!country) {
        country = await this.fetchCountry();
        if (country) this.writeCachedCountry(country);
      }
      if (country !== this.targetCountry) return;
      window.setTimeout(() => this.open(), this.openDelayMs);
    }

    readCachedCountry() {
      try {
        const raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.c !== 'string') return null;
        if (Date.now() - parsed.t > this.cacheTtlMs) return null;
        return parsed.c;
      } catch (_) {
        return null;
      }
    }

    writeCachedCountry(country) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({ c: country, t: Date.now() }));
      } catch (_) { /* localStorage unavailable */ }
    }

    async fetchCountry() {
      try {
        const res = await fetch('https://api.country.is/', {
          method: 'GET',
          credentials: 'omit',
          cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data && typeof data.country === 'string' ? data.country.toUpperCase() : null;
      } catch (_) {
        return null;
      }
    }

    open() {
      this.previouslyFocused = document.activeElement;
      this.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.setAttribute('data-state', 'open');
        document.body.classList.add('geo-prompt-open');
        document.addEventListener('keydown', this.onKeydown);
        const primary = this.querySelector('.geo-prompt__btn--primary');
        if (primary) primary.focus();
      });
    }

    close() {
      this.setAttribute('data-state', 'closing');
      document.removeEventListener('keydown', this.onKeydown);
      window.setTimeout(() => {
        this.setAttribute('hidden', '');
        this.removeAttribute('data-state');
        document.body.classList.remove('geo-prompt-open');
        if (this.previouslyFocused && this.previouslyFocused.focus) {
          this.previouslyFocused.focus();
        }
      }, 200);
    }

    dismiss() {
      if (!this.testMode) this.setCookie(this.dismissCookie, this.dismissDays);
      this.close();
    }

    trapFocus(e) {
      const focusables = this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)'));
      return match ? match[2] : null;
    }

    setCookie(name, days) {
      const maxAge = days * 24 * 60 * 60;
      document.cookie = `${name}=1; max-age=${maxAge}; path=/; SameSite=Lax`;
    }
  });
}
